import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, User, Radio, Crown, PlaySquare, MessageCircle, Search, ChevronDown, ChevronLeft, ChevronRight, ArrowRight,
  Eye, Users, Star, Heart, BadgeCheck, SlidersHorizontal, Images, UserCheck, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PeepingLogo } from "@/components/PeepingLogo";
import { DevPreviewBanner } from "@/components/DevPreviewBanner";
import { MediaImage } from "@/components/Media";
import { OwnerReviewBanner, ReviewerBanner, CreatorViewBanner, NAV as MOBILE_NAV } from "@/components/SubscriberLayout";
import { FilterDrawer, ActiveChips, EMPTY_FILTERS, filterCreators, activeChips, removeChip } from "@/components/DiscoverFilters";

const BRAND_BANNER = "/peeping-hero.webp";

const TOP = [
  { to: "/app/discover", label: "Home", icon: Home }, { to: "/app/creators", label: "Creators", icon: User },
  { to: "/app/live", label: "Live Now", icon: Radio }, { to: "/app/membership", label: "Subscriptions", icon: Crown },
  { to: "/app/library", label: "My Library", icon: PlaySquare }, { to: "/app/messages", label: "Messages", icon: MessageCircle },
];
const FILTERS = [["all", "All"], ["new", "New"], ["featured", "Featured"], ["live", "Live Now"], ["top", "Top Rated"], ["az", "A-Z"]];
const pill = "rounded-full border border-[#8a2be2] bg-gradient-to-b from-[#5b16a8]/70 to-[#3a0d6e]/70 text-white transition-[background-color,box-shadow] hover:shadow-[0_0_18px_rgba(160,60,255,0.55)]";

function Img({ url, className }) {
  return url ? <MediaImage url={url} className={className} /> : <div className={`${className} bg-gradient-to-br from-[#2a0f3d] via-[#120818] to-[#050407]`} />;
}

function TopBar({ q, setQ, onSearch }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.username || "P").slice(0, 2).toUpperCase();
  return (
    <header data-testid="discover-header" className="sticky top-0 z-40 flex w-full items-center gap-3 border-b border-white/5 bg-[#050407]/95 px-4 py-3 backdrop-blur sm:gap-4 lg:px-6">
      <NavLink to="/app/discover" data-testid="discover-logo" className="shrink-0">
        <PeepingLogo className="w-[5.75rem] sm:w-28 lg:w-40" taglineClassName="mt-1 pl-[0.2em] text-[0.5rem] tracking-[0.2em] sm:text-[0.55rem] sm:tracking-[0.34em] lg:mt-1.5 lg:text-[0.62rem] lg:tracking-[0.45em]" />
      </NavLink>
      <nav className="hidden flex-1 items-center gap-1 xl:flex">
        {TOP.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} data-testid={`top-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) => `flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? "border border-[#a03cff] bg-[#3a0d6e]/60 text-white shadow-[0_0_14px_rgba(160,60,255,0.45)]" : "border border-transparent text-white/90 hover:text-[#d9b3ff]"}`}>
            <Icon size={17} className={label === "Live Now" ? "text-[#b56bff]" : ""} /> {label}
          </NavLink>
        ))}
      </nav>
      <form onSubmit={(e) => { e.preventDefault(); onSearch(); }} className="ml-auto flex min-w-0 flex-1 items-center rounded-full border border-white/10 bg-[#111015] px-4 py-2 xl:max-w-xs">
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="discover-search" placeholder="Search creators..." aria-label="Search creators" className="min-w-0 flex-1 bg-transparent text-base text-white placeholder:text-white/50 focus:outline-none md:text-sm" />
        <button type="submit" aria-label="Search" data-testid="discover-search-btn"><Search size={18} /></button>
      </form>
      <button onClick={() => navigate("/app/profile")} data-testid="discover-account" aria-label="Account" className="flex shrink-0 items-center gap-1">
        <span className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#a03cff] bg-[#2a0b4d] text-sm font-bold">{initials}</span>
        <ChevronDown size={16} className="hidden sm:block" />
      </button>
    </header>
  );
}

function SideBar({ go }) {
  const items = [["discover", "Discover", Eye], ["all", "All Creators", Users], ["live", "Live Now", Radio], ["new", "New Creators", Star],
    ["top", "Top Rated", Heart], ["following", "Following", UserCheck], ["subs", "My Subscriptions", Crown], ["library", "My Library", PlaySquare]];
  return (
    <aside className="hidden w-56 shrink-0 px-4 pt-5 lg:block">
      <ul className="sticky top-24 flex flex-col gap-1.5">
        {items.map(([k, label, Icon]) => (
          <li key={k}>
            <button onClick={() => go(k)} data-testid={`side-nav-${k}`}
              className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm transition-colors ${k === "discover" ? "border border-[#8a2be2] bg-gradient-to-r from-[#3a0d6e] to-[#1a0830] shadow-[0_0_16px_rgba(140,40,230,0.35)]" : "hover:bg-white/5"}`}>
              <Icon size={19} className={k === "discover" ? "text-[#c77dff]" : "text-white"} /> {label}
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );
}

function Hero({ slides, onExplore }) {
  const [i, setI] = useState(0);
  const [hover, setHover] = useState(false);
  const touch = useRef(null);
  const navigate = useNavigate();
  const n = slides.length;
  const go = useCallback((d) => setI((x) => (x + d + n) % n), [n]);
  useEffect(() => {
    if (hover || n < 2) return undefined;
    const t = setInterval(() => go(1), 6000);
    return () => clearInterval(t);
  }, [go, hover, n]);
  useEffect(() => { if (i >= n) setI(0); }, [i, n]);
  const s = slides[Math.min(i, n - 1)];
  return (
    <section data-testid="discover-hero" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - (touch.current ?? 0); if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1); }}
      className="relative aspect-[3/1] w-full overflow-hidden rounded-2xl border border-white/5 bg-[#050407]">
      {s.brand ? (
        <>
          <img src={BRAND_BANNER} alt="Explore incredible creators. Exclusive content. A more personal world. More views, more pleasure." data-testid="hero-brand-image" className="absolute inset-0 h-full w-full object-contain" />
          <button onClick={onExplore} data-testid="hero-cta" aria-label="Explore creators"
            className="absolute left-[4.45%] top-[67.3%] z-10 h-[12.3%] w-[24%] rounded-[1vw] focus-visible:outline focus-visible:outline-2 focus-visible:outline-white" />
        </>
      ) : (
        <button onClick={() => navigate(`/app/c/${s.handle}`)} aria-label={`View ${s.display_name}`} data-testid="hero-creator-slide" className="absolute inset-0 h-full w-full text-left">
          <Img url={s.banner_url} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-[#1d0630]/60 to-transparent" />
          <div key={s.key} className="relative z-10 flex h-full flex-col justify-center px-5 animate-in fade-in duration-700 sm:px-16 lg:px-24 lg:pr-80">
            <p className="text-[0.55rem] tracking-[0.4em] text-white/80 sm:text-sm">FEATURED CREATOR</p>
            <p className="mt-1 hidden line-clamp-2 text-lg font-extralight uppercase tracking-[0.2em] text-white sm:block sm:text-3xl">{s.headline || "Come closer"}</p>
            <p data-testid="hero-title" className="truncate bg-gradient-to-r from-[#a63cff] via-[#c13cf0] to-[#d42be8] bg-clip-text text-2xl font-black uppercase leading-none text-transparent sm:text-5xl">{s.display_name}</p>
            <span className="mt-2 flex w-fit items-center gap-2 rounded-lg bg-gradient-to-r from-[#8a2be2] to-[#b03cff] px-3 py-1.5 text-[0.6rem] font-semibold tracking-[0.15em] sm:mt-4 sm:px-6 sm:py-3 sm:text-sm">VIEW PROFILE <ArrowRight size={14} /></span>
          </div>
        </button>
      )}
      {n > 1 && (
        <>
          <button onClick={() => go(-1)} data-testid="hero-prev" aria-label="Previous" className="absolute left-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 hover:bg-black/80 sm:flex"><ChevronLeft size={22} /></button>
          <button onClick={() => go(1)} data-testid="hero-next" aria-label="Next" className="absolute right-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 hover:bg-black/80 sm:flex"><ChevronRight size={22} /></button>
        </>
      )}
      <div className="absolute bottom-[3%] left-1/2 z-20 flex -translate-x-1/2 gap-2">
        {n > 1 && slides.map((x, k) => <button key={x.key} onClick={() => setI(k)} aria-label={`Slide ${k + 1}`} data-testid={`hero-dot-${k}`} className={`h-2 w-2 rounded-full transition-colors sm:h-3 sm:w-3 ${k === i ? "bg-[#b03cff]" : "bg-white/35"}`} />)}
      </div>
    </section>
  );
}

function SectionHead({ icon: Icon, title, action, onAction, testid }) {
  return (
    <div className="mb-3 mt-8 flex items-center justify-between gap-3">
      <h2 className="flex items-center gap-3 text-base font-bold uppercase tracking-wide sm:text-xl"><Icon size={24} className="text-[#a63cff]" fill={Icon === Star ? "#a63cff" : "none"} /> {title}</h2>
      {action && <button onClick={onAction} data-testid={testid} className="flex items-center gap-1 text-sm text-[#c77dff] hover:text-white">{action} <ArrowRight size={16} /></button>}
    </div>
  );
}

const cardCls = "group overflow-hidden rounded-xl border border-[#3b1a5c] bg-[#0d0a12] transition-[border-color,box-shadow] hover:border-[#8a2be2] hover:shadow-[0_0_18px_rgba(140,40,230,0.35)]";

function FeaturedCard({ c }) {
  const navigate = useNavigate();
  return (
    <div data-testid={`featured-card-${c.handle}`} className={cardCls}>
      <div className="relative aspect-[4/3]">
        <Img url={c.card_url || c.avatar_url || c.cover_url} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d0a12] via-transparent to-transparent" />
        {c.live && <span title="Live now" className="absolute right-3 top-3 h-2.5 w-2.5 rounded-full bg-emerald-400 shadow-[0_0_8px_#34d399]" />}
        <div className="absolute bottom-2 left-3 right-3">
          <p className="flex items-center gap-1 truncate font-bold">{c.display_name} {c.verified && <BadgeCheck size={16} className="shrink-0 fill-[#3b82f6] text-white" />}</p>
          <p className="text-xs text-white/75"><span data-testid={`featured-followers-${c.handle}`}>{c.follower_count} {c.follower_count === 1 ? "follower" : "followers"}</span> · {c.post_count} {c.post_count === 1 ? "post" : "posts"}</p>
        </div>
      </div>
      <div className="p-2.5"><button onClick={() => navigate(`/app/c/${c.handle}`)} data-testid={`view-profile-${c.handle}`} className={`${pill} w-full py-1.5 text-sm`}>View Profile</button></div>
    </div>
  );
}

function LiveCard({ c }) {
  const navigate = useNavigate();
  const l = c.live;
  return (
    <div data-testid={`live-card-${c.handle}`} className={cardCls}>
      <div className="relative aspect-[4/3]">
        <Img url={c.card_url || c.avatar_url || c.cover_url} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d0a12] via-transparent to-transparent" />
        <span className="absolute left-2 top-2 flex items-center gap-1 rounded-md bg-[#8a2be2] px-2 py-0.5 text-xs font-bold"><span className="h-1.5 w-1.5 rounded-full bg-white" /> LIVE</span>
        {l.viewers !== null && <span data-testid={`live-viewers-${c.handle}`} className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-black/60 px-2 py-0.5 text-xs font-semibold"><Eye size={13} /> {l.viewers}</span>}
        <div className="absolute bottom-2 left-3 right-3">
          <p className="flex items-center gap-1.5 truncate font-bold"><span className="h-2 w-2 rounded-full bg-emerald-400" /> {c.display_name}</p>
          <p className="truncate text-xs text-white/80">{l.title}</p>
        </div>
      </div>
      <div className="p-2.5"><button onClick={() => navigate(l.locked ? "/app/membership" : `/app/live/${l.session_id}`)} data-testid={`watch-now-${c.handle}`} className={`${pill} w-full py-1.5 text-sm`}>{l.locked ? "Subscribe to watch" : "Watch Now"}</button></div>
    </div>
  );
}

function BrowseCard({ c }) {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(`/app/c/${c.handle}`)} data-testid={`browse-card-${c.handle}`} className={`${cardCls} relative aspect-[3/4] text-left`}>
      <Img url={c.card_url || c.avatar_url || c.cover_url} className="h-full w-full object-cover" />
      {c.live && <span className="absolute left-2 top-2 h-2.5 w-2.5 rounded-full bg-emerald-400 shadow-[0_0_8px_#34d399]" />}
      {c.is_new && <span className="absolute right-2 top-2 rounded-md bg-[#8a2be2]/90 px-1.5 py-0.5 text-[0.6rem] font-bold">NEW</span>}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 to-transparent p-2.5">
        <p className="flex items-center gap-1 truncate text-sm font-bold">{c.display_name} {c.verified && <BadgeCheck size={14} className="shrink-0 fill-[#3b82f6] text-white" />}</p>
        <p className="truncate text-[0.65rem] text-white/70">{c.headline || `@${c.handle}`}</p>
        <p data-testid={`browse-followers-${c.handle}`} className="text-[0.65rem] text-[#c77dff]">{c.follower_count} {c.follower_count === 1 ? "follower" : "followers"}{c.following ? " · Following" : ""}</p>
      </div>
    </button>
  );
}

const normQ = (q) => q.trim().replace(/^@+/, "").replace(/\s+/g, " ").toLowerCase();

function applyFilter(rows, f, q, adv) {
  const n = normQ(q);
  const compact = n.replace(/\s/g, "");
  let out = rows.filter((c) => !n || `${c.display_name} ${c.handle} ${c.headline || ""} ${(c.categories || []).join(" ")}`.toLowerCase().includes(n)
    || c.handle.toLowerCase().includes(compact) || c.display_name.toLowerCase().replace(/\s/g, "").includes(compact));
  if (f === "new") out = out.filter((c) => c.is_new).sort((a, b) => (b.joined_at || "").localeCompare(a.joined_at || ""));
  if (f === "featured") out = out.filter((c) => c.promo);
  if (f === "live") out = out.filter((c) => c.live);
  if (f === "top") out = [];
  if (f === "az") out = [...out].sort((a, b) => a.display_name.localeCompare(b.display_name));
  return filterCreators(out, adv);
}

function BrowseSearch({ q, setQ }) {
  return (
    <div data-testid="browse-search" className="mb-3 flex w-full items-center gap-2 rounded-full border border-[#3b1a5c] bg-[#111015] px-4 py-2.5 focus-within:border-[#a03cff] focus-within:shadow-[0_0_12px_rgba(160,60,255,0.35)]">
      <Search size={18} className="shrink-0 text-[#a63cff]" />
      <input value={q} onChange={(e) => setQ(e.target.value)} type="search" enterKeyHint="search" autoCapitalize="none" autoCorrect="off" data-testid="browse-search-input"
        placeholder="Search creators by name or username…" aria-label="Search creators by name or username" className="min-w-0 flex-1 bg-transparent text-base text-white placeholder:text-white/50 focus:outline-none md:text-sm [&::-webkit-search-cancel-button]:hidden" />
      {q && <button onClick={() => setQ("")} data-testid="browse-search-clear" aria-label="Clear search" className="shrink-0 rounded-full p-1 text-[#c7c1cf] hover:text-white"><X size={16} /></button>}
    </div>
  );
}

function Browse({ d, f, setF, q, setQ, browseRef }) {
  const [adv, setAdv] = useState(EMPTY_FILTERS);
  const [open, setOpen] = useState(false);
  const rows = useMemo(() => applyFilter(d.creators, f, q, adv), [d.creators, f, q, adv]);
  const chips = activeChips(adv);
  const filtered = f !== "all" || chips.length > 0 || !!q;
  return (
    <section ref={browseRef} data-testid="browse-section" className="scroll-mt-24">
      <div className="mb-3 mt-8">
        <h2 className="mb-3 flex items-center gap-3 text-base font-bold uppercase tracking-wide sm:text-xl"><Users size={24} className="text-[#a63cff]" /> Browse All Creators</h2>
        <BrowseSearch q={q} setQ={setQ} />
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map(([k, label]) => (
            <button key={k} onClick={() => setF(k)} data-testid={`filter-${k}`} className={`rounded-full border px-4 py-1.5 text-xs transition-colors ${f === k ? "border-[#a03cff] bg-[#3a0d6e]/70 shadow-[0_0_12px_rgba(160,60,255,0.45)]" : "border-white/15 bg-[#111015] hover:border-white/35"}`}>{label}</button>
          ))}
          <button onClick={() => setOpen(true)} data-testid="filters-toggle" className={`flex items-center gap-2 rounded-lg border px-4 py-1.5 text-xs ${chips.length ? "border-[#a03cff] bg-[#3a0d6e]/60" : "border-white/15 bg-[#111015]"}`}><SlidersHorizontal size={14} /> Filters{chips.length > 0 && <span data-testid="filters-count" className="rounded-full bg-[#8a2be2] px-1.5 text-[0.6rem] font-bold">{chips.length}</span>}</button>
        </div>
      </div>
      <FilterDrawer open={open} onOpenChange={setOpen} value={adv} onApply={setAdv} categories={d.categories || []}
        preview={(x) => applyFilter(d.creators, f, q, x).length} />
      <ActiveChips chips={chips} onRemove={(c) => setAdv(removeChip(adv, c))} onClear={() => setAdv(EMPTY_FILTERS)} />
      {f !== "top" && <p data-testid="browse-count" className="mb-3 text-xs text-[#a7a0af]">{rows.length} {rows.length === 1 ? "creator" : "creators"}{filtered ? ` of ${d.creators.length}` : ""}</p>}
      {f === "top" && <p data-testid="top-rated-note" className="rounded-xl border border-[#3b1a5c] bg-[#0d0a12] p-4 text-sm text-[#c7c1cf]">Ratings aren't collected on PEEPING yet, so there's no Top Rated ranking. We don't show invented scores.</p>}
      {f !== "top" && rows.length === 0 && <p data-testid="browse-empty" className="rounded-xl border border-[#3b1a5c] bg-[#0d0a12] p-4 text-sm text-[#c7c1cf]">{d.creators.length === 0 ? "New creators are coming soon." : q.trim() ? `No creators match “${q.trim()}”${f !== "all" || chips.length ? " with these filters" : ""}.` : "No creators match these filters yet."}{d.creators.length > 0 && q.trim() && <button onClick={() => setQ("")} data-testid="browse-empty-clear-search" className="ml-2 text-[#c77dff] underline">Clear search</button>}{d.creators.length > 0 && (chips.length > 0 || f !== "all") && <button onClick={() => { setAdv(EMPTY_FILTERS); setF("all"); }} data-testid="browse-empty-clear" className="ml-2 text-[#c77dff] underline">Clear filters</button>}</p>}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8">
        {rows.map((c) => <BrowseCard key={c.id} c={c} />)}
      </div>
    </section>
  );
}

function MobileNav() {
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 bg-[#050407]/95 px-2 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] backdrop-blur xl:hidden">
      <ul className="mx-auto flex max-w-xl justify-between">
        {MOBILE_NAV.map(({ to, label, icon: Icon }) => (
          <li key={to} className="flex-1"><NavLink to={to} data-testid={`discover-mobile-nav-${label.toLowerCase()}`} className={({ isActive }) => `flex flex-col items-center gap-1 text-[0.58rem] ${isActive ? "text-[#c77dff]" : "text-[#a7a0af]"}`}><Icon size={20} /> {label}</NavLink></li>
        ))}
      </ul>
    </nav>
  );
}

export default function Discover() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [f, setF] = useState("all");
  const [err, setErr] = useState(false);
  const browseRef = useRef(null);
  const load = useCallback(() => { api.get("/discover").then((r) => { setD(r.data); setErr(false); }).catch(() => setErr(true)); }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);
  const toBrowse = (k) => { if (k) setF(k); browseRef.current?.scrollIntoView({ behavior: "smooth" }); };
  const side = (k) => ({ discover: () => window.scrollTo({ top: 0, behavior: "smooth" }), all: () => toBrowse("all"), live: () => toBrowse("live"),
    new: () => toBrowse("new"), top: () => toBrowse("top"), subs: () => navigate("/app/membership"), following: () => navigate("/app/following"), library: () => navigate("/app/library") }[k]());
  const slides = useMemo(() => [{ key: "brand", brand: true }, ...(d?.carousel || []).map((c) => ({ ...c, key: c.id }))], [d]);
  return (
    <div data-testid="discover-screen" className="min-h-screen overflow-x-hidden bg-[#050407] text-white">
      <DevPreviewBanner />
      <CreatorViewBanner />
      <OwnerReviewBanner />
      <ReviewerBanner />
      <TopBar q={q} setQ={setQ} onSearch={() => toBrowse()} />
      <div className="flex">
        <SideBar go={side} />
        <main className="min-w-0 flex-1 px-4 pb-28 pt-5 lg:pr-6 lg:pl-0 xl:pb-10">
          <Hero slides={slides} onExplore={() => toBrowse("all")} />
          {!d && !err && <p className="mt-6 text-sm text-[#a7a0af]">Loading creators…</p>}
          {!d && err && <p data-testid="discover-error" className="mt-6 text-sm text-red-300">We couldn't load creators. <button onClick={load} data-testid="discover-retry" className="underline">Retry</button></p>}
          {d && (
            <>
              <SectionHead icon={Star} title="Featured Creators" action="View All" onAction={() => toBrowse("featured")} testid="featured-view-all" />
              {d.featured.length === 0 ? <p data-testid="featured-empty" className="rounded-xl border border-[#3b1a5c] bg-[#0d0a12] p-4 text-sm text-[#c7c1cf]">Incredible creators are coming soon.</p> : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">{d.featured.slice(0, 6).map((c) => <FeaturedCard key={c.id} c={c} />)}</div>
              )}
              <SectionHead icon={Radio} title="Live Now" action="View All Live" onAction={() => navigate("/app/live")} testid="live-view-all" />
              {d.live.length === 0 ? <p data-testid="live-empty" className="rounded-xl border border-[#3b1a5c] bg-[#0d0a12] p-4 text-sm text-[#c7c1cf]">No creators are live right now.</p> : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">{d.live.slice(0, 6).map((c) => <LiveCard key={c.id} c={c} />)}</div>
              )}
              <Browse d={d} f={f} setF={setF} q={q} setQ={setQ} browseRef={browseRef} />
            </>
          )}
          <p className="mt-10 flex items-center gap-2 text-[0.65rem] text-[#6f6878]"><Images size={12} /> All creators shown are real, active PEEPING creators. Counts and live status update automatically.</p>
        </main>
      </div>
      <MobileNav />
    </div>
  );
}
